// Or from '@reduxjs/toolkit/query' if not using the auto-generated hooks
import { BaseQueryApi, BaseQueryFn, createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ENV } from '@/utils/env';
import { RootState } from '@/redux/store';
import { SupportedApiLanguages } from '@/locales';

export enum ApiTagTypes {
    Category = 'Category',
    PricingRule = 'PricingRule',
    EntranceFee = 'EntranceFee',
    Grant = 'Grant',
    User = 'User',
    Brand = 'Brand',
    AutoBilling = 'AutoBilling',
    Menu = 'Menu',
    ModuleExternalPos = 'ModuleExternalPos',
    GuestGroup = 'GuestGroup',
    Customer = 'Customer',
    ReportGeneration = 'ReportGeneration',
    PosLayout = 'PosLayout',
    PosLayoutFunction = 'PosLayoutFunction',
    Tag = 'Tag',
    Product = 'Product',
    Library = 'Library',
    Sku = 'Sku',
    Steps = 'Steps',
    DynamicReport = 'DynamicReport',
    DynamicReportSavedFilters = 'DynamicReportSavedFilters',
    ExternalId = 'ExternalId',
    CrossSelling = 'CrossSelling',
    PosUsers = 'PosUsers',
    PosGroups = 'PosGroups',
    PosRoles = 'PosRoles',
    TableLayout = 'TableLayout',
    Room = 'Room',
    BrandUsers = 'BrandUsers',
    Channels = 'Channels',
    BrandsList = 'BrandsList',
    BadgeReader = 'BadgeReader',
    LoyaltyConfig = 'LoyaltyConfig',
    Webhook = 'Webhook',
    BrandPaymentConfiguration = 'BrandPaymentConfiguration',
    BrandRestaurantFamilies = 'BrandRestaurantFamilies',
    BrandRestaurants = 'BrandRestaurants',
    OperationZone = 'OperationZone',
    Restaurant = 'Restaurant',
    PromotionalBanner = 'PromotionalBanner',
    BrandPromotionalBanner = 'BrandPromotionalBanner',
    BrandPromotionalBannerSettings = 'BrandPromotionalBannerSettings',
    MfaMethods = 'MfaMethods',
    AllowedIps = 'AllowedIps',
    Shift = 'Shift',
}

const prepareHeaders = (
    headers: Headers,
    { getState }: Pick<BaseQueryApi, 'getState' | 'extra' | 'endpoint' | 'type' | 'forced'>,
) => {
    const token = (getState() as RootState).user?.accessToken;
    const hasBearerToken =
        (token && !headers.has('authorization')) || headers.get('authorization') === 'Bearer undefined';
    if (hasBearerToken) {
        headers.set('authorization', `Bearer ${token}`);
    }
    const { language } = (getState() as RootState).app;
    if (language) {
        headers.set('Io-Locale', SupportedApiLanguages[language]);
    }
    return headers;
};

// initialize an empty api service that we'll inject endpoints into later as needed
export const innovorderApi = createApi({
    baseQuery: fetchBaseQuery({
        baseUrl: ENV.API_URL,
        prepareHeaders,
    }),
    endpoints: () => ({}),
    tagTypes: Object.values(ApiTagTypes),
});

export const billingApi = createApi({
    baseQuery: fetchBaseQuery({
        baseUrl: ENV.BILLING_REQUEST_GATEWAY_URL,
        mode: 'cors',
        headers: {
            'Access-Control-Allow-Origin': '*',
        },
    }),
    endpoints: () => ({}),
    tagTypes: ['AutoBilling'],
});

/**
 * **Why we use this serializeReportsQuery?**
 *
 * Because our API used by BO-V1 and BO-V2
 * Expect a particular form of query params when a param has a list of possible values
 * For example: The user want to filter by restaurantIds = [781, 782]
 * The rtk query react will send params in the format `?restaurantIds=781,782`
 * But the API expects `?restaurantIds[]=781&restaurantIds[]=782`
 *
 * **How do we customize prams formatting?**
 *
 * We customize prams formatting using the paramsSerializer attributes in createApi
 * We use a separate API (reportV2Api) so we can format query params in this specific way
 * */
const serializeReportsQuery = (params: Record<string, string | number | string[] | number[]>) => {
    return Object.entries(params)
        .map(([key, value]) => {
            if (Array.isArray(value)) {
                return value.map((val) => `${encodeURIComponent(key)}[]=${encodeURIComponent(val)}`).join('&');
            }
            return value ? `${encodeURIComponent(key)}=${encodeURIComponent(value)}` : value;
        })
        .filter(Boolean)
        .join('&');
};

export const reportV2Api = createApi({
    reducerPath: 'reportV2Api',
    baseQuery: fetchBaseQuery({
        baseUrl: ENV.API_URL,
        prepareHeaders,
        paramsSerializer: serializeReportsQuery,
    }),
    endpoints: () => ({}),
    tagTypes: [ApiTagTypes.DynamicReport, ApiTagTypes.DynamicReportSavedFilters],
});

/**
 * **Why do we need a streaming API?**
 *
 * Some endpoints in our API return data as a stream of text/plain responses
 * rather than a single JSON response. This is useful for long-running operations
 * where we want to show progress updates to the user.
 *
 * For example, when enabling hierarchy for brands or synchronizing sales data,
 * the API streams status updates as the operation progresses.
 *
 * This custom streaming API implementation allows us to:
 * 1. Set the correct headers for streaming responses (Accept: text/plain)
 * 2. Handle the response as a ReadableStream rather than JSON
 * 3. Process chunks of data as they arrive rather than waiting for the full response
 */
const streamingBaseQuery: BaseQueryFn<{ url: string; method: string; body: any }, unknown, unknown> = async (
    { url, method, body },
    api,
) => {
    try {
        // Créer un objet Headers et ajouter les headers spécifiques au streaming
        const headers = new Headers({
            Accept: 'text/plain',
        });

        // Ajouter Content-Type si on a un body
        if (body) {
            headers.set('Content-Type', 'application/json');
        }

        // Réutiliser prepareHeaders pour ajouter le token et la locale
        const preparedHeaders = prepareHeaders(headers, api);

        const response = await fetch(ENV.API_URL + url, {
            headers: preparedHeaders,
            method,
            body: body ? JSON.stringify(body) : undefined,
        });

        if (!response.body) {
            throw new Error('No response body');
        }

        return {
            data: response.body,
        };
    } catch (error) {
        return { error };
    }
};

export const innovorderStreamingApi = createApi({
    reducerPath: 'innovorderStreamingApi',
    baseQuery: streamingBaseQuery,
    endpoints: () => ({}),
});
